import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/imgs/Jhonks-main-logo.png';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaGlobe,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowUp
} from 'react-icons/fa';
import { newsletterService } from '../../services/newsletterService';
import playStoreIcon from '../../assets/imgs/playstore.png';
import appStoreIcon from '../../assets/imgs/apple.png';

const Footer = () => {
  const playStoreLink = 'https://play.google.com/store/apps/details?id=com.jhonks.jhonksmobile';
  const appStoreLink = 'https://www.apple.com/app-store/';

  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterType, setNewsletterType] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle footer link click with scroll to top
  const handleFooterLinkClick = () => {
    // Small delay to allow route change, then scroll to top
    setTimeout(() => {
      scrollToTop();
    }, 100);
  };

  // Show/hide scroll to top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const footerLinks = {
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Our Mission', href: '/mission' },
      { name: 'Careers', href: '/careers' },
      { name: 'Contact Us', href: '/contact' },
    ],
    services: [
      { name: 'Material Collection', href: '/services/collection' },
      { name: 'Agent Network', href: '/services/agents' },
      { name: 'Waste Management', href: '/services/waste-management' },
      { name: 'Sustainability', href: '/services/sustainability' },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Cookie Policy', href: '/cookies' },
      { name: 'Data Protection', href: '/data-protection' },
    ],
  };

  const socialLinks = [
    { name: 'Twitter', href: 'https://x.com/jhonksofficial', icon: FaTwitter },
    { name: 'Facebook', href: 'https://web.facebook.com/people/Jhonks/61560227128245/', icon: FaFacebook },
    // { name: 'Instagram', href: 'https://www.instagram.com/jhonksofficial/', icon: FaInstagram },
    { name: 'LinkedIn', href: 'https://www.linkedin.com/company/jhonks/', icon: FaLinkedin },
  ];

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubscribing(true);
    setNewsletterMessage('');

    try {
      await newsletterService.subscribe(email);
      setNewsletterMessage('Thank you for subscribing! You will receive our latest updates.');
      setNewsletterType('success');
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterMessage(error.response?.data?.message || 'Error subscribing. Please try again.');
      setNewsletterType('error');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info & Logo */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              <img
                className="h-20 w-auto"
                src={logo}
                alt="Jhonks Logo"
              />
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-6 max-w-md">
              Jhonks is revolutionizing waste management by connecting sellers with verified agents 
              for efficient material collection and sustainable recycling practices.
            </p>
            
            {/* Contact Information */}
            <div className="space-y-3">
              <div className="flex items-center text-gray-300 text-sm">
                <FaPhone className="h-4 w-4 mr-3 flex-shrink-0" />
                <span>+234 814 844 4929</span>
              </div>
              <div className="flex items-center text-gray-300 text-sm">
                <FaEnvelope className="h-4 w-4 mr-3 flex-shrink-0" />
                <span>service@jhonks.com</span>
              </div>
              <div className="flex items-start text-gray-300 text-sm">
                <FaMapMarkerAlt className="h-4 w-4 mr-3 flex-shrink-0 mt-0.5" />
                <span>Lagos, Nigeria</span>
              </div>
              <div className="flex items-center text-gray-300 text-sm">
                <FaGlobe className="h-4 w-4 mr-3 flex-shrink-0" />
                <span>www.jhonks.com</span>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={handleFooterLinkClick}
                    className="footer-link text-gray-300 text-sm hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Services
            </h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={handleFooterLinkClick}
                    className="footer-link text-gray-300 text-sm hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Stay Updated
            </h3>
            <p className="text-gray-300 text-sm mb-4">
              Subscribe to our newsletter for the latest updates and news.
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  required
                />
              </div>
              
              {newsletterMessage && (
                <div className={`flex items-center text-xs ${
                  newsletterType === 'success' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {newsletterType === 'success' ? (
                    <FaCheckCircle className="h-3 w-3 mr-1" />
                  ) : (
                    <FaExclamationTriangle className="h-3 w-3 mr-1" />
                  )}
                  {newsletterMessage}
                </div>
              )}
              
              <button
                type="submit"
                disabled={isSubscribing}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>

            <div className="mt-4 flex items-center gap-2">
              <a
                href={playStoreLink}
                target="_blank"
                rel="noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-gray-700 bg-gray-800 px-2 py-2 text-xs font-semibold text-white hover:bg-gray-700 transition-colors"
              >
                <img src={playStoreIcon} alt="Google Play" className="h-4 w-4 object-contain" />
                Play Store
              </a>
              <a
                href={appStoreLink}
                target="_blank"
                rel="noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-gray-700 bg-gray-800 px-2 py-2 text-xs font-semibold text-white hover:bg-gray-700 transition-colors"
              >
                <img src={appStoreIcon} alt="App Store" className="h-4 w-4 object-contain" />
                App Store
              </a>
            </div>
          </div>

          

          
        </div>

        {/* Social Media */}
        <div className="mt-8 pt-8 border-t border-gray-800">
          <div className="flex justify-center">
            {/* Social Media */}
            <div className="flex items-center space-x-4">
              <span className="text-gray-300 text-sm font-medium">Follow us:</span>
              <div className="flex space-x-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    className="social-icon text-gray-400 hover:text-white transition-colors duration-200"
                    aria-label={social.name} rel="noreferrer"
                  >
                    <social.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="bg-gray-950 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* Copyright */}
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © {currentYear} Jhonks Environmental Solutions. All rights reserved.
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center md:justify-end space-x-6">
              {footerLinks.legal.map((link, index) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={handleFooterLinkClick}
                  className="footer-link text-gray-400 text-sm hover:text-white transition-colors duration-200"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-4 pt-4 border-t border-gray-800">
            <div className="text-center text-gray-500 text-xs">
              <p>
                Jhonks is committed to environmental sustainability and responsible waste management practices.
              </p>
              <p className="mt-1">
                Building a greener future.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-green-600 hover:bg-green-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 ease-in-out transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 z-50"
          aria-label="Scroll to top"
        >
          <FaArrowUp className="h-5 w-5" />
        </button>
      )}
    </footer>
  );
};

export default Footer;